import React, { useState, useEffect } from 'react'
import { SelectOutlined, SearchOutlined, CloseOutlined } from '@ant-design/icons'
import { Button, Col, Drawer, Form, Input, Row, Select, Tabs, Tag, Modal, List, Spin, message } from 'antd'
import { CreateStore, StoreCard } from '@a4b/api/src/modules/Monetisation/Puja/types'
import { lazy } from 'react'
import { A4B_APPS } from '@/utils/constants'

import { formatJson } from '@/utils/helper'
import { categoryFilterDataTypes, categoryItemTypes, customItemTypes, dataTypes, declarativeItemTypes, sectionTypes, staticUIItemTypes } from '../../../utils/constants'
import { withSuspense } from '@/hoc/WithSuspense'
import { renderSectionComponent } from './Helper/RenderSections'
import S3Upload, { AllowedFormats } from '@a4b/ui/src/modules/monetisation/puja/molecules/S3Upload'
import { PUJA_PRESIGN_URL, YODA_CDN_URL } from '@/utils/constants'
import { useAppContext } from '@/components/AppContext'

const ScheduleSection = lazy(() => import('./ScheduleSection'))

const { TextArea } = Input
const { Option } = Select

interface Props {
  onClose: (fetchSection?: boolean | React.MouseEvent | React.KeyboardEvent) => void
  visible: boolean
  cdnUrl: string
  preSignUrl: string
  formSubmithandler: (values: any) => void
  form: any
  initialValues: StoreCard
  isEditJourney: boolean
  storeList: CreateStore[]
  onFormChange: (formValues: any) => void
  openSelectionWindow: () => void
  sortedData: any[]
  componentRender: any
  getInventoryList: (storeId: string) => void
  inventoryList: any
}

interface Category {
  id: string;
  name: string;
}

const otherItemTypes = [
  { value: 'sku', label: 'SKU' },
  { value: 'store', label: 'Store' },
];

const SectionCreation: React.FC<Props> = ({
  onClose,
  visible,
  cdnUrl,
  preSignUrl,
  formSubmithandler,
  form,
  initialValues,
  isEditJourney,
  storeList,
  onFormChange,
  openSelectionWindow,
  componentRender,
  sortedData,
  getInventoryList,
  inventoryList
}) => {
  console.log(form.getFieldsValue(), "afwserwerwerwer")
  const [activeTab, setActiveTab] = useState("1");
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<{ id: string, name: string } | null>(null);
  const [categories, setCategories] = useState<any>([]);
  const [filteredCategories, setFilteredCategories] = useState<any>([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);

  const { networkInstance, app, countryState } = useAppContext();
  const monetisationPujaApi = app === A4B_APPS.SRIMANDIR
    ? networkInstance.clientWithHeaders.monetisationPujaApi
    : networkInstance.clientWithHeaders.monetisationAstroPujaApi;

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const handleSectionTypeChange = (value: string) => {
    if (value === 'custom') {
      form.setFieldsValue({
        item_type: 'puja_stories',
        data: {
          type: 'puja_stories'
        }
      });
    }
  };

  const fetchCategories = async () => {
    setCategoryLoading(true);
    try {
      const response = await monetisationPujaApi.storesApi.getCategories({
        limit: 1000,
        offset: 0,
      });
      const categoryList = response?.data?.data?.categories || [];
      setCategories(categoryList);
      setFilteredCategories(categoryList);
      setCategoriesLoaded(true);
    } catch (error) {
      console.error('Error fetching categories:', error);
      message.error('Failed to fetch categories');
    } finally {
      setCategoryLoading(false);
    }
  };

  // Load categories on component mount for edit flow
  useEffect(() => {
    if (isEditJourney && initialValues?.data?.category_id && !categoriesLoaded) {
      fetchCategories();
    }
  }, [isEditJourney, initialValues?.data?.category_id, categoriesLoaded]);

  // Load selected category on edit journey after categories are loaded
  useEffect(() => {
    if (isEditJourney && initialValues?.data?.category_id && categoriesLoaded && !selectedCategory) {
      const existingCategory = categories.find((cat: any) => cat.id === initialValues.data.category_id);
      if (existingCategory) {
        setSelectedCategory({ id: existingCategory.id, name: existingCategory.name });
      }
    }
  }, [isEditJourney, initialValues?.data?.category_id, categories, categoriesLoaded, selectedCategory]);

  useEffect(() => {
    if (categoryModalVisible && !categoriesLoaded) {
      fetchCategories();
    }
  }, [categoryModalVisible, categoriesLoaded]);

  useEffect(() => {
    const filtered = categories.filter((category: any) =>
      category.name.toLowerCase().includes(searchText.toLowerCase())
    );

    if (selectedCategory) {
      const selectedIndex = filtered.findIndex((cat: any) => cat.id === selectedCategory.id);
      if (selectedIndex > 0) {
        const selectedCat = filtered.splice(selectedIndex, 1)[0];
        filtered.unshift(selectedCat);
      }
    }

    setFilteredCategories(filtered);
  }, [searchText, categories, selectedCategory]); // ✅ Added selectedCategory as dependency

  const handleCategorySelect = (category: any) => {
    setSelectedCategory({ id: category.id, name: category.name });
    form.setFieldsValue({
      data: {
        ...form.getFieldValue('data'),
        category_id: category.id
      }
    });
    setCategoryModalVisible(false);
  };

  // Handle category removal
  const handleRemoveCategory = () => {
    setSelectedCategory(null);
    form.setFieldsValue({
      data: {
        ...form.getFieldValue('data'),
        category_id: undefined
      }
    });
  };

  const openCategoryModal = () => {
    setCategoryModalVisible(true);
    setSearchText('');
  };

  const closeCategoryModal = () => {
    setCategoryModalVisible(false);
    setSearchText('');
  };

  // Reset states when drawer closes
  useEffect(() => {
    if (!visible) {
      setSelectedCategory(null);
      setCategories([]);
      setFilteredCategories([]);
      setCategoriesLoaded(false);
      setSearchText('');
    }
  }, [visible]);

  return (
    <Drawer
      title={`${isEditJourney ? "Update" : "Create New"}  Section`}
      placement='right'
      onClose={onClose}
      visible={visible}
      destroyOnClose={true}
      width={850}
      size='large'
    >
      <Form
        form={form}
        layout='vertical'
        labelCol={{ span: 12 }}
        wrapperCol={{ span: 12 }}
        initialValues={initialValues}
        onValuesChange={onFormChange}
      >
        <div style={{ width: '100%' }}>
          <Form.Item
            label='Title'
            name='title'
            labelCol={{ span: 22 }}
            wrapperCol={{ span: 22 }}
            rules={[{ required: true, message: 'Enter title' }]}
          >
            <Input placeholder='Enter title' />
          </Form.Item>
          <Form.Item
            label='Description'
            name='description'
            labelCol={{ span: 22 }}
            wrapperCol={{ span: 22 }}
            rules={[
              {
                required: true,
                message: 'Enter description of the category',
              },
            ]}
          >
            <TextArea placeholder='Enter description' rows={2} />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={() => true}
          >
            {({ getFieldValue, setFieldsValue }) => (
              <>
                <Form.Item
                  labelCol={{ span: 22 }}
                  wrapperCol={{ span: 22 }}
                  name="target_config"
                  label={<Row justify='space-between' style={{ width: '100%' }}>
                    <span>Target Config</span>
                    <Tag
                      onClick={() => {
                        const prettyJson = formatJson(getFieldValue("target_config"))
                        setFieldsValue({ "target_config": prettyJson })
                      }}
                      style={{
                        cursor: 'pointer',
                        marginLeft: '20px',
                        zIndex: 1000
                      }}
                      color="blue"
                    >Pretty</Tag></Row>}
                  rules={[
                    {
                      required: false,
                      message: 'Please Enter Target Config',
                    },
                    {
                      validator: async () => {
                        const targetConfig = getFieldValue("target_config")
                        try {
                          if (targetConfig)
                            JSON.parse(targetConfig)
                        } catch (e) {
                          throw new Error(`${String(e)}`)
                        }
                      },
                    }
                  ]}
                >
                  <TextArea rows={2} />
                </Form.Item>
              </>
            )}
          </Form.Item>
          <Row style={{ width: '100%', gap: 50 }}>
            <Form.Item
              label={`Position`}
              name='position'
              labelCol={{ span: 18 }}
              wrapperCol={{ span: 18 }}
              rules={[{ required: true, message: 'Please input position!' }]}
              style={{ flexBasis: '28%' }}
            >
              <Input type={'number'} placeholder={`Position`} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              label={`Min app version`}
              name='min_app_version'
              labelCol={{ span: 18 }}
              wrapperCol={{ span: 18 }}
              rules={[{ required: false }]}
              style={{ flexBasis: '30%' }}
            >
              <Input type={'number'} placeholder={`Min app version`} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              label={`Variant`}
              name='variant'
              labelCol={{ span: 18 }}
              wrapperCol={{ span: 18 }}
              rules={[{ required: false }]}
              style={{ flexBasis: '25%', marginLeft: '15px' }}
            >
              <Input placeholder={`Min app Ver`} style={{ width: '100%' }} />
            </Form.Item>
          </Row>

          <Row style={{ width: '100%', gap: 50 }}>
            <Form.Item
              label='Section Type'
              name='section_type'
              style={{ flexBasis: '28%' }}
              rules={[
                { required: true, message: 'Please select section type!' },
              ]}
              labelCol={{ span: 18 }}
              wrapperCol={{ span: 18 }}
            >
              <Select
                style={{ width: '100%' }}
                placeholder='Please select section type'
                disabled={isEditJourney}
                onChange={handleSectionTypeChange}
              >
                {sectionTypes.map(item => (
                  <Option key={item.value} value={item.value}>{item.label}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => {
                return prevValues.section_type !== currentValues.section_type
              }}
              labelCol={{ span: 22 }}
              wrapperCol={{ span: 22 }}
            >
              {({ getFieldValue }) => (
                <Form.Item
                  label="Item Type"
                  name="item_type"
                  rules={[
                    { required: true, message: 'Please select item type!' },
                  ]}
                  style={{ flexBasis: '30%', width: '100%' }}
                  labelCol={{ span: 18 }}
                  wrapperCol={{ span: 18 }}
                >
                  <Select
                    placeholder="Select item type"
                    allowClear
                    style={{ width: '100% !important' }}
                    disabled={getFieldValue('section_type') === 'data' || getFieldValue('section_type') === 'custom' || isEditJourney}
                  >
                    {(() => {
                      const sectionType = getFieldValue('section_type');
                      let options = [];

                      switch (sectionType) {
                        case 'declarative':
                          options = declarativeItemTypes;
                          break;
                        case 'static_ui':
                          options = staticUIItemTypes;
                          break;
                        case 'custom':
                          options = customItemTypes;
                          break;
                        case 'category':
                          options = categoryItemTypes
                          break;
                        case 'data':
                        default:
                          options = otherItemTypes;
                          break;
                      }

                      return options.map((item) => (
                        <Option value={item.value} key={item.value}>
                          {item.label}
                        </Option>
                      ));
                    })()}
                  </Select>
                </Form.Item>
              )}
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.section_type !== currentValues.section_type ||
                prevValues.item_type !== currentValues.item_type
              }
              labelCol={{ span: 22 }}
              wrapperCol={{ span: 22 }}
            >
              {({ getFieldValue }) => {
                const sectionType = getFieldValue('section_type');
                const itemType = getFieldValue('item_type');

                const showDataTypeDropdown =
                  sectionType === 'data' ||
                  sectionType === 'declarative' ||
                  sectionType === 'custom' ||
                  (sectionType === 'category' && itemType === 'filters');

                if (!showDataTypeDropdown) return null;

                return (
                  <Form.Item
                    label='Data type'
                    name={['data', 'type']}
                    rules={[{ required: true, message: 'Enter Data type' }]}
                    labelCol={{ span: 22 }}
                    wrapperCol={{ span: 22 }}
                    style={{ flexBasis: '25%' }}
                  >
                    <Select
                      placeholder='Please select Data type'
                      style={{ maxHeight: '200px', overflowY: 'auto', width: '200px' }}
                      disabled={sectionType === 'custom'}
                    >
                      {sectionType === 'category' && itemType === 'filters'
                        ? categoryFilterDataTypes.map(item => (
                          <Option key={item.value} value={item.value}>{item.label}</Option>
                        ))
                        : dataTypes.map(item => (
                          <Option key={item.value} value={item.value}>{item.label}</Option>
                        ))
                      }
                    </Select>
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Row>
        </div>

        <Form.Item
          name={['data', 'category_id']}
          style={{ display: 'none' }}
        >
          <Input />
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.section_type !== currentValues.section_type ||
            prevValues.item_type !== currentValues.item_type
          }
        >
          {({ getFieldValue }) => {
            if (getFieldValue('section_type') === 'custom' && getFieldValue('item_type') === 'puja_stories') {
              return (
                <div style={{ marginBottom: 24 }}>
                  <Form.Item
                    label="Header"
                    name={['data', 'puja_stories', 'header']}
                    labelCol={{ span: 22 }}
                    wrapperCol={{ span: 22 }}
                    rules={[{ required: true, message: 'Please enter header' }]}
                  >
                    <Input placeholder="Enter header" />
                  </Form.Item>
                  <Form.Item
                    label="Subtext"
                    name={['data', 'puja_stories', 'subtext']}
                    labelCol={{ span: 22 }}
                    wrapperCol={{ span: 22 }}
                    rules={[{ required: true, message: 'Please enter subtext' }]}
                  >
                    <Input placeholder="Enter subtext" />
                  </Form.Item>
                  <Form.Item
                    label="Icon URL"
                    name={['data', 'puja_stories', 'icon_url']}
                    labelCol={{ span: 14 }}
                    wrapperCol={{ span: 14 }}
                    style={{ marginTop: '10px', width: '30%' }}
                    rules={[
                      {
                        required: true,
                        message: 'Please upload Media',
                      },
                    ]}
                    valuePropName='fileList'
                  >
                    <S3Upload
                      cdnUrl={cdnUrl}
                      preSignUrl={preSignUrl}
                      allowedFileExtension={[
                        AllowedFormats.PNG,
                        AllowedFormats.JPEG,
                        AllowedFormats.WEBP,
                      ]}
                      min={0}
                      max={1}
                      maxSizeInKiloBytes={9145728}
                    />
                  </Form.Item>
                </div>
              );
            }
            return null;
          }}
        </Form.Item>

        <Tabs defaultActiveKey="1" activeKey={activeTab} onChange={handleTabChange}>
          <Tabs.TabPane tab="Default config" key="1">
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => (prevValues.section_type !== currentValues.section_type ||
                prevValues.item_type !== currentValues.item_type
              )}
              labelCol={{ span: 22 }}
              wrapperCol={{ span: 22 }}
            >
              {({ getFieldValue }) => {
                const sectionType = getFieldValue('section_type')
                const itemType = getFieldValue('item_type')
                const isStaticUiItemType = staticUIItemTypes?.map(itemType => itemType?.value)?.includes(itemType)
                const scheduledDataPath = ["data"]

                switch (sectionType) {
                  case 'data':
                    return <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) => prevValues?.data?.type !== currentValues?.data?.type}
                      labelCol={{ span: 22 }}
                      wrapperCol={{ span: 22 }}
                    >
                      {({ getFieldValue }) => renderSectionComponent(getFieldValue('data')?.type, {
                        cdnUrl,
                        preSignUrl,
                        storeList,
                        ...(getFieldValue('data')?.type === 'sankalp_banner' && {
                          getInventoryList
                        }),
                        inventoryList,
                        form,
                        basePath: scheduledDataPath
                      })}
                    </Form.Item>
                  case 'static_ui':
                    if (isStaticUiItemType)
                      return <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => prevValues?.data?.type !== currentValues?.data?.type}
                        labelCol={{ span: 22 }}
                        wrapperCol={{ span: 22 }}
                      >
                        {renderSectionComponent(itemType, {
                          cdnUrl,
                          preSignUrl,
                          form,
                          basePath: scheduledDataPath
                        })}
                      </Form.Item>
                }
              }}
            </Form.Item>

            <Col span={12}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.item_type !== currentValues.item_type ||
                  prevValues.section_type !== currentValues.section_type
                }
                labelCol={{ span: 22 }}
                wrapperCol={{ span: 22 }}
              >
                {({ getFieldValue }) => {
                  const sectionType = getFieldValue('section_type');
                  const itemType = getFieldValue('item_type');
                  const isListType = sectionType === 'list_item_small' || sectionType === 'list_item_large';
                  const showFields = (sectionType === 'hero' || isListType) && itemType === 'store';

                  return showFields && (
                    <>
                      {isListType && (
                        <>
                          <Form.Item
                            label='Title'
                            name={['data', 'title']}
                            labelCol={{ span: 22 }}
                            wrapperCol={{ span: 22 }}
                            rules={[{ required: false, message: 'Enter title' }]}
                          >
                            <Input placeholder='Enter title' />
                          </Form.Item>
                          <Form.Item
                            label='Description'
                            name={['data', 'description']}
                            labelCol={{ span: 22 }}
                            wrapperCol={{ span: 22 }}
                            rules={[{ required: false, message: 'Enter description' }]}
                          >
                            <Input placeholder='Enter description' />
                          </Form.Item>
                        </>
                      )}
                      <Button
                        onClick={openSelectionWindow}
                        style={{ marginTop: '10px' }}
                        type='primary'
                        icon={<SelectOutlined />}
                      >
                        Select Store
                      </Button>
                    </>
                  )
                }}
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.item_type !== currentValues.item_type ||
                  prevValues.section_type !== currentValues.section_type
                }
                labelCol={{ span: 22 }}
                wrapperCol={{ span: 22 }}
              >
                {({ getFieldValue }) =>
                  (getFieldValue('section_type') === 'hero' ||
                    getFieldValue('section_type') === 'list_item_small' ||
                    getFieldValue('section_type') === 'list_item_large') &&
                  getFieldValue('item_type') === 'sku' && (
                    <Button
                      onClick={openSelectionWindow}
                      style={{ marginTop: '20px' }}
                      type='primary'
                      icon={<SelectOutlined />}
                    >
                      Select Store Card
                    </Button>
                  )
                }
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.section_type !== currentValues.section_type ||
                  prevValues.item_type !== currentValues.item_type
                }
                labelCol={{ span: 22 }}
                wrapperCol={{ span: 22 }}
              >
                {({ getFieldValue }) => {
                  const sectionType = getFieldValue('section_type');
                  const itemType = getFieldValue('item_type');

                  if (sectionType === 'category' && itemType === 'filters') {
                    return (
                      <div>
                        <Button
                          onClick={openCategoryModal}
                          style={{ marginTop: '20px' }}
                          type='primary'
                          icon={<SelectOutlined />}
                        >
                          Select Category
                        </Button>

                        {/* Display selected category */}
                        {selectedCategory && (
                          <div style={{
                            marginTop: '12px',
                            padding: '8px 12px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '6px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div>
                              <strong>{selectedCategory.name}</strong>
                              <br />
                              <span style={{ color: '#666', fontSize: '12px' }}>ID: {selectedCategory.id}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }
                  else if (sectionType === 'category') {
                    return (
                      <Button
                        onClick={openSelectionWindow}
                        style={{ marginTop: '20px' }}
                        type='primary'
                        icon={<SelectOutlined />}
                      >
                        Select Category
                      </Button>
                    );
                  }

                  return null;
                }}
              </Form.Item>
            </Col>

            {sortedData?.length > 0 && <Form.Item
              labelCol={{ span: 22 }}
              wrapperCol={{ span: 22 }}
            >
              <Col span={32} style={{ overflow: 'scroll', marginTop: '10px', padding: '10px', border: '1px solid #bfbfbf' }}>
                {sortedData?.map(item => (
                  <Col key={item?.id} span={24}>
                    {React.cloneElement(componentRender, { data: item })}
                  </Col>
                ))}
              </Col>
            </Form.Item>}
          </Tabs.TabPane>
          <Tabs.TabPane tab="Scheduled config" key="2">
            {activeTab === "2" && withSuspense(
              <ScheduleSection
                inventoryList={inventoryList}
                getInventoryList={getInventoryList}
                storeList={storeList}
                sectionForm={form}
              />
            )}
          </Tabs.TabPane>
        </Tabs>

        {activeTab === "1" &&
          <Button type="primary" onClick={formSubmithandler} style={{ marginTop: '30px' }}>
            {isEditJourney ? 'Update Section' : 'Create Section'}
          </Button>
        }
      </Form>

      <Modal
        title="Select Category"
        visible={categoryModalVisible}
        onCancel={closeCategoryModal}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Input
          placeholder="Search categories..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ marginBottom: 16 }}
        />

        <Spin spinning={categoryLoading}>
          <List
            dataSource={filteredCategories}
            renderItem={(category: any) => (
              <List.Item
                onClick={() => handleCategorySelect(category)}
                style={{
                  cursor: 'pointer',
                  backgroundColor: selectedCategory?.id === category.id ? '#e6f7ff' : 'transparent',
                  padding: '12px 16px',
                  borderRadius: '4px',
                  marginBottom: '4px',
                  border: '1px solid transparent'
                }}
                onMouseEnter={(e) => {
                  if (selectedCategory?.id !== category.id) {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedCategory?.id !== category.id) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <List.Item.Meta
                  title={category.name}
                  description={`ID: ${category.id}`}
                />
              </List.Item>
            )}
            style={{ maxHeight: '400px', overflow: 'auto' }}
          />
        </Spin>
      </Modal>
    </Drawer>
  )
}

export default React.memo(SectionCreation)
